import { useEffect, useState } from 'react';
import { Button } from '@/kit/Button';
import { InputField } from '@/kit/FormField';
import { RequiredMark } from '@/kit/RequiredMark';
import { Icon } from '@/kit/Icon';
import { Select } from '@/kit/Select';
import { DialogClose, DialogContent, type DialogComponentProps } from '@/kit/dialog';
import type { FirmAccountantRow } from '@/core/models/firm-settings.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { firmSettingsService } from '@/core/services/firmSettings';
import {
  ACCOUNTANT_CHAR_MODE,
  ACCOUNTANT_EXTRA_CHARS,
  ACCOUNTANT_MAX_LENGTH,
  ACCOUNTANT_TEXT_FIELDS,
  NEW_ACCOUNTANT_FORM,
  accountantFieldInvalid,
  isOtherAccountingBody,
  accountantFormValid,
  accountantPayload,
  type AccountantForm,
  type AccountantTextField,
} from '@/features/settings/accountantForm';
import { validateForm } from '@/features/settings/validateForm';

/**
 * BUG-031: Accountant's Report > "Add new accountant" (3.0 had a + beside the Accountant picker). The accountant is
 * one of the firm's accountants, so it is saved exactly as Settings > Accountants saves one (same fields, rules and
 * endpoint) and closes with the saved row for the report to select.
 */
export function AddAccountantDialog({ dialogRef }: DialogComponentProps<null, FirmAccountantRow>) {
  const [form, setForm] = useState<AccountantForm>(NEW_ACCOUNTANT_FORM);
  const [touched, setTouched] = useState<Set<string>>(() => new Set());
  const [accountingBodies, setAccountingBodies] = useState<string[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    firmSettingsService.getAccountantOptions().then(
      (options) => {
        if (cancelled) return;
        setAccountingBodies(options.accountingBodies);
        setCountries(options.countries);
      },
      (error) => {
        if (!cancelled) notifyError(error);
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  // The API trims the name, so a name of spaces only is empty (the shared form rule counts its characters).
  const nameBlank = form.name.trim().length === 0;

  const onSave = () => {
    if (isSaving) return;
    if (!validateForm(accountantFormValid(form) && !nameBlank)) {
      setTouched(new Set(ACCOUNTANT_TEXT_FIELDS));
      return;
    }
    setIsSaving(true);
    firmSettingsService.saveAccountant(accountantPayload(form, null)).then(
      (row) => {
        notifySuccess('Accountant added successfully');
        dialogRef.close(row);
      },
      (error) => {
        setIsSaving(false);
        notifyError(error);
      },
    );
  };

  const markTouched = (field: string) => setTouched((current) => new Set(current).add(field));

  const textField = (field: AccountantTextField, label: string, className: string, required = false) => (
    <div className="form-field">
      <p className="fw-bold">
        {label}
        {required && <RequiredMark />}
      </p>
      <InputField
        fieldClassName={className}
        autoComplete="off"
        name={field}
        required={required || undefined}
        maxLength={ACCOUNTANT_MAX_LENGTH[field]}
        charMode={ACCOUNTANT_CHAR_MODE[field]}
        extraChars={ACCOUNTANT_EXTRA_CHARS[field]}
        value={form[field]}
        invalid={touched.has(field) && (accountantFieldInvalid(form, field) || (field === 'name' && nameBlank))}
        onChange={(event) => setForm((current) => ({ ...current, [field]: event.target.value }))}
        onBlur={() => markTouched(field)}
      />
    </div>
  );

  return (
    <div className="mat-dialog-wrapper">
      <DialogContent>
        <form noValidate onSubmit={(event) => event.preventDefault()}>
          <div className="main-contain">
            <div className="sidenav-header">
              <div className="d-flex align-items-center justify-space-between">
                <h2 className="m-0">
                  <span>Add New Accountant</span>
                </h2>
                <DialogClose>
                  <Icon name="close" className="close" />
                </DialogClose>
              </div>
            </div>
          </div>
          <div>
            {textField('name', 'Accountant Name', 'wp-100', true)}
            {textField('addressLine', 'Address', 'wp-100')}
            {textField('addressLine2', 'Address line 2', 'wp-100')}
            <div className="d-flex gap-20">
              {textField('city', 'City', 'w-200')}
              {textField('postCode', 'Post Code', 'w-200')}
            </div>
            <div className="d-flex gap-20">
              {textField('county', 'County', 'w-200')}
              <div className="form-field">
                <p className="fw-bold">Country</p>
                <Select
                  className="w-200"
                  items={countries}
                  value={form.country}
                  onChange={(value) => setForm((current) => ({ ...current, country: value as string | null }))}
                />
              </div>
            </div>
            <div className="form-field">
              <p className="fw-bold">Accounting Body</p>
              <Select
                className="w-200"
                items={accountingBodies}
                value={form.accountingBody}
                onChange={(value) =>
                  setForm((current) => ({
                    ...current,
                    accountingBody: value as string | null,
                    accountingBodyName: isOtherAccountingBody(value as string | null) ? current.accountingBodyName : '',
                  }))
                }
              />
            </div>
            {/* BUG-015: as in 3.0, Other/None asks for the accounting body's name (max 20). */}
            {isOtherAccountingBody(form.accountingBody) &&
              textField('accountingBodyName', 'Accounting body name', 'w-200')}
          </div>
          <div className="d-flex gap-15 hr-top pt-1 mt-1">
            <Button className="action-button primary-button" busy={isSaving} onClick={onSave}>
              Save
            </Button>
            <DialogClose as="button" type="button" className="action-button secondary-button">
              Cancel
            </DialogClose>
          </div>
        </form>
      </DialogContent>
    </div>
  );
}
